<script>
    import { onMount, onDestroy } from 'svelte';
    import pb from '../../lib/pocketbase';
    import { getEgresoDets } from '../../models/egresoDetModel';
    import { authStore } from '../../lib/authStore';
    import { get } from 'svelte/store';
    
    export let selectedItem;

    // Selected corredores array
    let selectedCorredores = [];
    let selectedPickers = [];
    let lastPickedValues = new Map();
    let animationTimeout = new Map();
    let refreshInterval;
    let isAdmin = false;
    let deliveryMethods = ['Despacho', 'Repartidor', 'Transporte'];

    // Helper function to format dates
    function formatDate(dateStr) {
      const date = new Date(dateStr);
      if (isNaN(date)) return '';
  
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
  
      return `${day}-${month}-${year}`;
    }

    // Check if user is admin
    async function checkUserRole() {
      const { authData } = get(authStore);
      if (authData) {
        const userData = JSON.parse(authData);
        try {
          const role = await pb.collection('role').getOne(userData.role);
          isAdmin = role.descrip === 'Administrador';
        } catch (error) {
          console.error('Error fetching role:', error);
          isAdmin = false;
        }
      }
    }

    // Update delivery method
    async function updateDeliveryMethod(newMethod) {
      if (!isAdmin) return;
      
      try {
        await pb.collection('egresoCab').update(selectedItem.id, {
          delivery_method: newMethod
        });
        selectedItem.delivery_method = newMethod;
      } catch (error) {
        console.error('Error updating delivery method:', error);
      }
    }
  
    // Calculate the total weight
    $: totalPeso = selectedItem?.egresoDet?.reduce((sum, egreso) => sum + (egreso.peso || 0), 0) || 0;

    // Get corredor names for display
    function getCorredorNames() {
      if (!selectedCorredores || selectedCorredores.length === 0) return 'Ninguno seleccionado';
      
      if (!selectedItem.corredores || !Array.isArray(selectedItem.corredores) || selectedItem.corredores.length === 0) {
        return 'Ninguno seleccionado';
      }
      
      const names = selectedItem.corredores
        .map(e => {
          if (e.expand && e.expand.corredor && e.expand.corredor.nombre) {
            return e.expand.corredor.nombre;
          }
          return null;
        })
        .filter(Boolean)
        .join(', ');
        
      return names || 'Ninguno seleccionado';
    }

    // Check if picked value changed for animation
    function isPickedChanged(egreso) {
      const lastValue = lastPickedValues.get(egreso.id);
      const currentValue = egreso.picked;
      
      if (lastValue !== currentValue) {
        if (animationTimeout.has(egreso.id)) {
          clearTimeout(animationTimeout.get(egreso.id));
        }
        
        const timeout = setTimeout(() => {
          lastPickedValues.set(egreso.id, currentValue);
          animationTimeout.delete(egreso.id);
        }, 2000);
        
        animationTimeout.set(egreso.id, timeout);
        return true;
      }
      return false;
    }

    // Function to refresh egresoDet data
    async function refreshEgresoDet() {
      if (selectedItem?.id) {
        const updatedEgresoDet = await getEgresoDets(selectedItem.id, 1, 100);
        if (updatedEgresoDet) {
          selectedItem.egresoDet = updatedEgresoDet.items;
        }
      }
    }

    // Initialize when component mounts
    onMount(async () => {
      await checkUserRole();
      // Initial fetch
      await refreshEgresoDet();
      
      // Set up periodic refresh
      refreshInterval = setInterval(refreshEgresoDet, 2000); // Refresh every 2 seconds
    });

    // Cleanup on destroy
    onDestroy(() => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
      animationTimeout.forEach(timeout => clearTimeout(timeout));
      animationTimeout.clear();
    });

    // Initialize lastPickedValues when egresoDet changes
    $: if (selectedItem?.egresoDet) {
      selectedItem.egresoDet.forEach(egreso => {
        if (!lastPickedValues.has(egreso.id)) {
          lastPickedValues.set(egreso.id, egreso.picked);
        }
      });
    }

    // Load existing corredores if any
    function loadExistingCorredores() {
      console.log("Loading existing corredores from:", selectedItem.corredores);
      
      if (selectedItem && selectedItem.corredores) {
        // Ensure it's an array
        if (Array.isArray(selectedItem.corredores)) {
          // Extract just the corredor IDs from the objects
          selectedCorredores = selectedItem.corredores.map(corredorItem => {
            // If it's the expanded structure from the JSON
            if (corredorItem.corredor) {
              return corredorItem.corredor;
            }
            // If it's already just the ID
            else if (typeof corredorItem === 'string') {
              return corredorItem;
            }
            // If it has an id property (fallback)
            else if (corredorItem.id) {
              return corredorItem.id;
            }
            return null;
          }).filter(Boolean); // Remove any null values
        } else {
          // Handle case where corredores might be an object or string
          try {
            // Try to parse it if it's a string representation of array
            const parsed = typeof selectedItem.corredores === 'string' 
              ? JSON.parse(selectedItem.corredores) 
              : selectedItem.corredores;
            
            if (Array.isArray(parsed)) {
              // Extract IDs using the same mapping logic
              selectedCorredores = parsed.map(corredorItem => {
                if (corredorItem.corredor) {
                  return corredorItem.corredor;
                } else if (typeof corredorItem === 'string') {
                  return corredorItem;
                } else if (corredorItem.id) {
                  return corredorItem.id;
                }
                return null;
              }).filter(Boolean);
            } else {
              selectedCorredores = [];
            }
          } catch (e) {
            console.error("Error parsing corredores:", e);
            selectedCorredores = [];
          }
        }
        
        console.log("Existing corredores loaded:", selectedCorredores);
      } else {
        selectedCorredores = [];
      }
    }
    // Call on component initialization
    loadExistingCorredores();

    function getPickerNames() {
      if (!selectedPickers || selectedPickers.length === 0) return 'Ninguno seleccionado';
      
      if (!selectedItem.pickers || !Array.isArray(selectedItem.pickers) || selectedItem.pickers.length === 0) {
        return 'Ninguno seleccionado';
      }
      
      const names = selectedItem.pickers
        .map(e => {
          if (e.expand && e.expand.picker && e.expand.picker.name) {
            return e.expand.picker.name;
          }
          return null;
        })
        .filter(Boolean)
        .join(', ');
        
      return names || 'Ninguno seleccionado';
    }

    function loadExistingPickers() {
      console.log("Loading existing pickers from:", selectedItem.pickers);
      
      if (selectedItem && selectedItem.pickers) {
        // Ensure it's an array
        if (Array.isArray(selectedItem.pickers)) {
          // Extract just the picker IDs from the objects
          selectedPickers = selectedItem.pickers.map(pickerItem => {
            // If it's the expanded structure from the JSON
            if (pickerItem.picker) {
              return pickerItem.picker;
            }
            // If it's already just the ID
            else if (typeof pickerItem === 'string') {
              return pickerItem;
            }
            // If it has an id property (fallback)
            else if (pickerItem.id) {
              return pickerItem.id;
            }
            return null;
          }).filter(Boolean); // Remove any null values
        } else {
          // Handle case where pickers might be an object or string
          try {
            // Try to parse it if it's a string representation of array
            const parsed = typeof selectedItem.pickers === 'string'
              ? JSON.parse(selectedItem.pickers)
              : selectedItem.pickers;
            
            if (Array.isArray(parsed)) {
              // Extract IDs using the same mapping logic
              selectedPickers = parsed.map(pickerItem => {
                if (pickerItem.picker) {
                  return pickerItem.picker;
                } else if (typeof pickerItem === 'string') {
                  return pickerItem;
                } else if (pickerItem.id) {
                  return pickerItem.id;
                }
                return null;
              }).filter(Boolean);
            } else {
              selectedPickers = [];
            }
          } catch (e) {
            console.error("Error parsing pickers:", e);
            selectedPickers = [];
          }
        }
        
        console.log("Existing pickers loaded:", selectedPickers);
      } else {
        selectedPickers = [];
      }
    }
    // Call on component initialization
    loadExistingPickers();
    
</script>
  
  <div class="space-y-2">
    <div><strong>Nombre:</strong> {selectedItem.nombre}</div>
    <div><strong>Fecha:</strong> {formatDate(selectedItem.fecha)}</div>
    <div><strong>NIT:</strong> {selectedItem.nit}</div>
    <div><strong>Número:</strong> {selectedItem.numero}</div>
    <div><strong>Estado:</strong> {selectedItem.sta}</div>
    <div><strong>Peso Total:</strong> {totalPeso} Lb</div>
    <div><strong>Correo:</strong>  {selectedItem.correo}</div>
    <div><strong>Pickers:</strong> {getPickerNames()}</div>
    <div><strong>Corredores:</strong> {getCorredorNames()}</div>
    <div class="flex items-center">
      <strong>Método de entrega:</strong>
      {#if isAdmin}
        <select 
          class="ml-2 px-2 py-1 border rounded"
          value={selectedItem.delivery_method}
          on:change={(e) => updateDeliveryMethod(e.target.value)}
        >
          {#each deliveryMethods as method}
            <option value={method}>{method}</option>
          {/each}
        </select>
      {:else}
        <span class="ml-2">{selectedItem.delivery_method}</span>
      {/if}
    </div>
    <div><strong>Persona que recibe:</strong> {selectedItem.nombre_recibe}</div>
    <div><strong>Teléfono de contacto:</strong> {selectedItem.tel_recibe}</div>
    <div><strong>Observaciones:</strong> {selectedItem.observ}</div>
  
    <!-- Egreso Detalles Table -->
    {#if selectedItem.egresoDet?.length > 0}
      <div class="mt-4">
        <h3 class="text-lg font-semibold text-blueGray-700 mb-2">Detalle del pedido</h3>
        <table class="w-full bg-white border border-gray-200 rounded-lg shadow-md">
          <thead class="bg-blueGray-100">
            <tr>
              <th class="py-2 px-4 text-left font-medium text-blueGray-700">SKU</th>
              <th class="py-2 px-4 text-left font-medium text-blueGray-700">Descripción</th>
              <th class="py-2 px-4 text-left font-medium text-blueGray-700">Cantidad</th>
              <th class="py-2 px-4 text-left font-medium text-blueGray-700">Colectados</th>
              <th class="py-2 px-4 text-left font-medium text-blueGray-700">Peso (Lb)</th>
            </tr>
          </thead>
          <tbody>
            {#each selectedItem.egresoDet as egreso}
              <tr class="border-t hover:bg-blueGray-50">
                <td class="py-2 px-4 text-blueGray-600">{egreso.sku}</td>
                <td class="py-2 px-4 text-blueGray-600">{egreso.descrip}</td>
                <td class="py-2 px-4 text-blueGray-600">{egreso.cantidad}</td>
                <td class="py-2 px-4 text-blueGray-600">
                  <span 
                    class:animate-pulse={isPickedChanged(egreso)}
                    class:bg-green-200={isPickedChanged(egreso)}
                    class:rounded={isPickedChanged(egreso)}
                    class:px-2={isPickedChanged(egreso)}
                    class:font-bold={isPickedChanged(egreso)}
                  >
                    {egreso.picked}
                  </span>
                </td>
                <td class="py-2 px-4 text-blueGray-600">{egreso.peso}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {:else}
      <div class="mt-4 text-blueGray-600">No hay detalles de egreso disponibles.</div>
    {/if}
  </div>

<style>
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.7;
      transform: scale(1.05);
    }
  }
  .animate-pulse {
    animation: pulse 1s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
</style>